import React, { Component } from 'react';
import { UserProfile } from 'fitbeat.gym-core';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { IOrganisationConfig, RemoteConfigVariables } from 'fitbeat.models';
import { translate } from 'react-i18next';
import {
  View, StyleSheet, Text, TouchableOpacity,
} from 'react-native';
import Theme from '../theme';
import {
  changeNutritionDay, getMealPlan, getImage, nutritionTabSelected, setNutritionPreference,
  mealRowPressed,
} from './actions';
import {
  toggleMealModal, toggleFoodPreferencesModal,
  toggleNutritionStepsModal,
  AppStateFocusedEvents,
  LoadingModal,
} from '../ui';
import { openPaywallModalAction } from '../payments/redux/payments-actions';
import { AbExperimentVariant } from '../ui/components/ABFeatureToggle';
import { UserDataConsumer } from '../utils';
import { GoogleAnalytics } from '../services';
import { ChevronPlayIcon } from '../ui/components/Icons'
import MealsForDay from './components/MealsForDay';
import { PaywallPage } from '../auth';

type PlaceholderTypeWhenConvertedToTsx = any;

interface IProps {
  user: PlaceholderTypeWhenConvertedToTsx;
  navigation: PlaceholderTypeWhenConvertedToTsx;
  isModalVisible: PlaceholderTypeWhenConvertedToTsx;
  isNutritionStepsModalVisible: PlaceholderTypeWhenConvertedToTsx;
  toggleNutritionStepsModal: PlaceholderTypeWhenConvertedToTsx;
  toggleMealModal: PlaceholderTypeWhenConvertedToTsx;
  openPaywallModal: PlaceholderTypeWhenConvertedToTsx;
  mealRowPressed: PlaceholderTypeWhenConvertedToTsx;
  getImage: PlaceholderTypeWhenConvertedToTsx;
  changeNutritionDay: PlaceholderTypeWhenConvertedToTsx;
  toggleFoodPreferencesModal: PlaceholderTypeWhenConvertedToTsx;
  nutritionTabSelected: PlaceholderTypeWhenConvertedToTsx;
  day: PlaceholderTypeWhenConvertedToTsx;
  getMealPlan: PlaceholderTypeWhenConvertedToTsx;
  t: PlaceholderTypeWhenConvertedToTsx;
  mealPlans: PlaceholderTypeWhenConvertedToTsx;
  nutritionPreference: PlaceholderTypeWhenConvertedToTsx;
  isLoading: PlaceholderTypeWhenConvertedToTsx;
  setNutritionPreference: PlaceholderTypeWhenConvertedToTsx;
  organisationConfig: IOrganisationConfig;
}

interface IState {
  days: PlaceholderTypeWhenConvertedToTsx[];
}

class Nutrition1Day extends Component<IProps, IState> {
  private googleAnalytics = new GoogleAnalytics();

  constructor(props) {
    super(props);
    this.state = {
      days: this.getDaysFromTodayToAWeekAhead(),
    };
  }

  public componentDidMount() {
    if (!this.props.user.userProfile.mealPreferences) {
      this.props.navigation.navigate('NutritionOnBoarding');
    }
    if (this.props.organisationConfig?.nutritionConfig?.orderMealsSupplier) {
      this.props.navigation.navigate('NutritionTabsOrderMeals');
    }
    this.screenFocussed();
  }

  public toggleMealModal = async () => {
    const { isModalVisible } = this.props;
    this.props.toggleMealModal(!isModalVisible);
  }

  public toggleNutritionStepsModal = () => {
    const { isNutritionStepsModalVisible } = this.props;
    this.props.toggleNutritionStepsModal(!isNutritionStepsModalVisible);
  }

  public closeMealModal = () => {
    this.props.toggleMealModal(false);
  }

  public setCurrentMealAndOpenMealModal = async (mealOfDay, currentMeal) => {
    const { user: { userProfile } } = this.props;

    if (!UserProfile.hasActivePlan(userProfile)) {
      this.props.openPaywallModal(this.onPaywallJoinSuccess, this.onPaywallModalClose);
      this.googleAnalytics.logEventWithParamaters('paywall_nutrition');
    } else {
      this.props.mealRowPressed(mealOfDay, currentMeal);
      this.props.getImage(currentMeal._id);
      this.toggleMealModal()
        .catch(() => { /* ignored */ });
    }
  }

  public setNewDay = (newDay) => {
    this.props.changeNutritionDay(moment(newDay.date));
    this.closeMealModal();
  }

  public getDaysFromTodayToAWeekAhead = () => {
    const days: PlaceholderTypeWhenConvertedToTsx[] = [];
    const today = moment();

    for (let i = 1; i <= 7; i += 1) {
      let date = moment().isoWeekday(i);
      if (date.diff(today) < 0) {
        date = date.add(7, 'days');
      }

      const day = {
        date: date.valueOf(),
        dayIndex: i === 7 ? 0 : i,
        displayName: date.format('dd'),
        dayOfWeek: date.format('dddd'),
        display: `${date.format('dddd')} ${date.format('D MMM')}`,
        value: date.format('dddd'),
      };

      days.push(day);
    }

    return days;
  }

  public openFoodPrefModalIfPreferenceOrDietGoalNotSet = () => {
    const { user: { userProfile } } = this.props;
    const { mealPreferences = {} } = userProfile;
    if (!mealPreferences.foodPreference || !mealPreferences.dietGoal) {
      this.props.toggleFoodPreferencesModal(true);
    }
  }

  public onPaywallJoinSuccess = () => {
    this.openFoodPrefModalIfPreferenceOrDietGoalNotSet();
  }

  public onPaywallModalClose = () => {
    this.openFoodPrefModalIfPreferenceOrDietGoalNotSet();
  }

  public screenFocussed = () => {
    this.props.getMealPlan();
    this.props.nutritionTabSelected('Recipes');
    this.setNewDay({ date: moment() });
    this.setState({ days: this.getDaysFromTodayToAWeekAhead() });
  }

  public render() {
    const {
      t,
      mealPlans,
      nutritionPreference,
      isLoading,
      user,
      navigation,
    } = this.props;
    const { mealPreferences } = user.userProfile;
    const nutritionPrev = nutritionPreference === undefined ? mealPreferences : nutritionPreference;
    if (nutritionPreference === undefined) {
      this.props.setNutritionPreference(mealPreferences);
    }
    const { day } = this.props;

    if (isLoading) {
      return <LoadingModal style={styles.loading} />;
    }

    return (
      <View className='full-height' style={styles.container}>
        <AppStateFocusedEvents
          onScreenFocus={this.screenFocussed}
          navigation={navigation}
        />

        {mealPlans && (
          <MealsForDay
            days={this.state.days}
            getDays={this.getDaysFromTodayToAWeekAhead}
            day={day}
            changeDay={this.setNewDay}
            mealPlans={mealPlans}
            mealPreferences={nutritionPrev}
            openMealModal={this.setCurrentMealAndOpenMealModal}
            closeMealModal={this.closeMealModal}
            toggleFoodPreferencesModal={this.toggleNutritionStepsModal}
          />
        )}
        {
          mealPlans && (
            <AbExperimentVariant
              remoteConfigKey={RemoteConfigVariables.learn_more_about_nutrition_button_visible}
              value={true}
            >
              <TouchableOpacity
                style={styles.learnAboutFBNutritionContainer}
                onPress={this.toggleNutritionStepsModal}
              >
                <Text style={styles.learnAboutFBNutritionText}>
                  {t('learnAboutFBNutrition').toUpperCase()}
                </Text>
                <ChevronPlayIcon width={35} height={35} color={Theme.PRIMARY} />
              </TouchableOpacity>
            </AbExperimentVariant>
          )
        }
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.GREY_LIGHT,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    backgroundColor: Theme.GREY_LIGHT,
  },
  learnAboutFBNutritionContainer: {
    height: 80,
    paddingVertical: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  learnAboutFBNutritionText: {
    color: Theme.PRIMARY,
    fontSize: 20,
    fontFamily: Theme.Custom700,
    marginHorizontal: 10,
  },
});

const mapStateToProps = (state) => {
  const { nutrition } = state;
  const { mealPlans, isLoading, nutritionPreference } = nutrition;

  return {
    mealPlans,
    isLoading,
    nutritionPreference,
    isNutritionStepsModalVisible: state.ui.nutritionStepsVisible,
    isDieticianModalVisible: state.ui.dieticianModalVisible,
    day: nutrition.viewingMealsForDay,
    organisationConfig: state.auth.user.enabledFeatures.organisationConfig
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { user: { nutritionService } } = ownProps;
  return {
    toggleMealModal: (visible) => dispatch(toggleMealModal(visible)),
    toggleNutritionStepsModal: (visible) => dispatch(toggleNutritionStepsModal(visible)),
    toggleFoodPreferencesModal: (visible) => dispatch(toggleFoodPreferencesModal(visible)),
    getImage: (mealId) => dispatch(getImage(nutritionService, mealId)),
    setNutritionPreference: (nutritionPreference) => dispatch(setNutritionPreference(nutritionPreference)),
    getMealPlan: () => dispatch(getMealPlan(nutritionService)),
    changeNutritionDay: (newDay) => dispatch(changeNutritionDay(newDay)),
    nutritionTabSelected: (tabTitle) => dispatch(nutritionTabSelected(tabTitle)),
    mealRowPressed: (mealOfDay, meal) => dispatch(mealRowPressed(mealOfDay, meal)),
    openPaywallModal: (onJoinSuccessCallback, onModalCloseCallback) => dispatch(
      openPaywallModalAction(PaywallPage.Nutrition, onJoinSuccessCallback, onModalCloseCallback),
    ),
  };
};

const Nutrition1DayConnected = UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(Nutrition1Day));
export default translate(['nutrition'], { wait: true })(Nutrition1DayConnected);
